import { LoginResponseWithSecret } from '@/Login';
import useLanguage from '@/utils/hooks/useLanguage';
import { CopyButton } from '@mantine/core';
import cn from 'classnames';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { MdCheck, MdContentCopy } from 'react-icons/md';
import { TbPasswordMobilePhone } from 'react-icons/tb';
import OTPInput from 'react-otp-input';
import { LoginFormInput } from '../form/schema/loginSchema';
import Modal, { ModalClose, ModalProps } from './Modal';

interface TwoFactorAuthModalProps extends ModalProps {
    userRequires2FA?: LoginResponseWithSecret;
    manualOnSubmit(): void;
    cancelTwoFactorAuth(): void;
}

const TwoFactorAuthModal = (props: TwoFactorAuthModalProps) => {
    const { userRequires2FA, manualOnSubmit, cancelTwoFactorAuth } = props;
    const lang = useLanguage();

    const {
        watch,
        setValue,
        clearErrors,
        formState: { isSubmitting }
    } = useFormContext<LoginFormInput>();

    const blob = useMemo(() => {
        if (userRequires2FA?.qrCode.byteLength) return new Blob([userRequires2FA.qrCode]);
        return null;
    }, [userRequires2FA]);

    const otp = watch('twoFactorToken');

    return (
        <Modal
            {...props}
            size={blob ? 'max-w-md' : 'max-w-sm'}
            overlayOpacity="bg-opacity-100"
            className={cn('overflow-y-auto', {
                'h-auto': !blob
            })}>
            <div className="absolute top-4 right-4">
                <ModalClose handlers={props.handlers} onClick={cancelTwoFactorAuth} />
            </div>
            <div className="text-brand-text p-4 py-8 xs:p-4 xs:py-8 text-center flex-col items-center overflow-y-auto">
                <div className="space-y-4">
                    <div className="w-16 h-16 bg-brand-neutral-700 rounded-full flex items-center justify-center mx-auto mb-6">
                        <TbPasswordMobilePhone className="text-brand-primary-light" size={32} strokeWidth={1.5} />
                    </div>
                    <div className="space-y-2">
                        <h1 className="text-xl sm:text-2xl font-semibold text-brand-text mb-2 tracking-tight">
                            {blob ? lang.twoFactorAuth.title : lang.twoFactorAuth.enterVerificationCode}
                        </h1>
                        {!blob && (
                            <p
                                className={cn('text-brand-neutral-400 text-sm leading-relaxed', {
                                    '': blob,
                                    'px-2 xs:px-10 mb-8': !blob
                                })}>
                                {blob ? lang.twoFactorAuth.followInstructions : lang.twoFactorAuth.openAuthenticatorApp}
                            </p>
                        )}
                    </div>
                    {blob && (
                        <ol className="list-decimal ml-3 text-start space-y-4 text-sm">
                            <li className="space-y-2">
                                <p>{lang.twoFactorAuth.toCompleteSetup}</p>
                                <ul className="space-y-2 text-primary-300">
                                    <li>
                                        <a
                                            className="hover:cursor-pointer text-brand-primary-light hover:text-brand-primary w-fit"
                                            href="https://support.google.com/accounts/answer/1066447"
                                            target="_blank"
                                            rel="noreferrer">
                                            {lang.twoFactorAuth.googleAuthenticator}
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            className="hover:cursor-pointer text-brand-primary-light hover:text-brand-primary w-fit"
                                            href="https://www.microsoft.com/en-my/security/mobile-authenticator-app"
                                            target="_blank"
                                            rel="noreferrer">
                                            {lang.twoFactorAuth.microsoftAuthenticator}
                                        </a>
                                    </li>
                                </ul>
                            </li>
                            <li className="space-y-4">
                                <p>{lang.twoFactorAuth.scanTheQRCodeOrManually}</p>

                                <div className="flex flex-col justify-center mr-auto relative w-28 rounded-sm">
                                    <img src={blob ? URL.createObjectURL(blob) : ''} />
                                </div>
                                {userRequires2FA?.secret && (
                                    <div className="text-sm flex justify-between items-start w-full px-2 py-1 gap-1 border border-brand-neutral-600 rounded-sm shadow-sm placeholder-brand-neutral-500">
                                        <p className="break-words break-all">{userRequires2FA.secret}</p>
                                        <CopyButton value={userRequires2FA.secret || ''}>
                                            {({ copied, copy }) => (
                                                <button onClick={copy} type="button" className="rounded-md p-1">
                                                    {copied ? (
                                                        <MdCheck className="w-4 h-4 text-brand-primary" />
                                                    ) : (
                                                        <MdContentCopy className="w-4 h-4 text-brand-neutral-200" />
                                                    )}
                                                </button>
                                            )}
                                        </CopyButton>
                                    </div>
                                )}
                            </li>
                            <li className="space-y-4">
                                <p>{lang.twoFactorAuth.enterTheSixDigitCode}</p>
                            </li>
                        </ol>
                    )}
                    <div
                        className={cn('pb-1', {
                            'pt-4': !blob
                        })}>
                        <OTPInput
                            value={otp || ''}
                            shouldAutoFocus
                            onChange={(val) => {
                                setValue('twoFactorToken', val);
                                clearErrors();
                                if (val.length === 6) manualOnSubmit();
                            }}
                            numInputs={6}
                            renderInput={(props) => (
                                <input
                                    disabled={isSubmitting}
                                    {...props}
                                    className={cn(
                                        props.className,
                                        '!w-8 h-8 xs:!w-10 xs:h-10 outline outline-brand-neutral-600 focus:outline-brand-primary bg-brand-background-dark rounded-sm text-lg text-brand-text'
                                    )}
                                />
                            )}
                            containerStyle={cn('space-x-2 xs:space-x-3', {
                                'justify-start px-3': blob,
                                'justify-center': !blob
                            })}
                            inputType="tel"
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default TwoFactorAuthModal;
